import { splitScreenOptions } from '../options/split-screen.options';

export const splitScreenDirective = {
  options: splitScreenOptions,
  container: new Map(),

  init(element, customOptions = {}) {
    const config = { ...splitScreenOptions, ...customOptions };
    const { selectors, threshold, resizeDebounce, mobileBreakpoint } = config;

    const items = Array.from(element.querySelectorAll(selectors.item));
    if (!items.length) return;

    const state = {
      element,
      items,
      config,
      currentIndex: 0,
      headerHeight: this.getHeaderHeight(selectors.header),
      itemsContainer: element.querySelector(selectors.itemsContainer),
      mediaWrappers: items.map(item => item.querySelector(selectors.mediaWrapper)),
      contentWrappers: items.map(item => item.querySelector(selectors.contentWrapper)),
      observers: {},
      rafId: null,
      isMobile: window.innerWidth < mobileBreakpoint,
      animateOnMobile: element.classList.contains('-stack-on-mobile')
    };

    element.style.setProperty('--header-offset', `${state.headerHeight}px`);

    // Apply decreasing z-index values to media wrappers.”
    this.setMediaWrappersZIndex(state);

    this.setupScrollListener(state);
    this.setupResizeObserver(state, resizeDebounce);

    items[0]?.classList.add('-active');
    this.updateActiveItem(state);
    this.updateStickyState(state);
    this.container.set(element.ambid, state);
  },

  getHeaderHeight(selector) {
    const headers = document.querySelectorAll(selector);
    return Array.from(headers).reduce((total, h) => total + (h.offsetHeight || 0), 0);
  },

  setMediaWrappersZIndex(state) {
    const { mediaWrappers } = state;

    mediaWrappers.forEach((wrapper, index) => {
      if (wrapper) {
        if (state.isMobile) {
          // set z-index on mobile
          const baseZIndex = 1000;
          const decrement = 10;
          const zIndex = baseZIndex - (index * decrement);
          wrapper.style.zIndex = zIndex;
        } else {
          // remove z-index
          wrapper.style.zIndex = '';
        }
      }
    });
  },

  shouldAnimate(state) {
    return !state.isMobile || state.animateOnMobile;
  },

  setupScrollListener(state) {
    const onScroll = () => {
      if (state.rafId) return;

      state.rafId = requestAnimationFrame(() => {
        if (this.shouldAnimate(state)) {
          this.updateActiveItem(state);
          this.updateStickyState(state);
        }
        state.rafId = null;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    state.scrollHandler = onScroll;
  },

  setupResizeObserver(state, debounceDelay) {
    let resizeTimeout;
    const { config } = state;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const wasMobile = state.isMobile;
        state.isMobile = window.innerWidth < config.mobileBreakpoint;
        state.headerHeight = this.getHeaderHeight(config.selectors.header);
        state.element.style.setProperty('--header-offset', `${state.headerHeight}px`);

        if (wasMobile !== state.isMobile) {
          this.removeAllSticky(state);
          this.removeAllActive(state);
          // Update z-index when switching between mobile and desktop
          this.setMediaWrappersZIndex(state);
        }

        this.updateActiveItem(state);
        this.updateStickyState(state);
      }, debounceDelay);
    };

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(document.body);
      state.observers.resize = resizeObserver;
    } else {
      window.addEventListener('resize', handleResize, { passive: true });
      state.resizeHandler = handleResize;
    }
  },

  /**
   * Sticky media height on mobile (CSS `--media-height-mobile`, e.g. `43vh`).
   *
   * @param {object} state
   * @returns {number}
   */
  getMobileMediaHeight(state) {
    const raw = getComputedStyle(state.element).getPropertyValue('--media-height-mobile').trim();
    const value = parseFloat(raw);

    if (!value) {
      return window.innerHeight * 0.43;
    }

    if (raw.endsWith('vh')) {
      return (value / 100) * window.innerHeight;
    }

    return value;
  },

  updateActiveItem(state) {
    if (!this.shouldAnimate(state)) return;

    const { element, contentWrappers, headerHeight, config, items } = state;
    const viewportHeight = window.innerHeight;
    const isMobileStack = state.isMobile && state.animateOnMobile;

    // Desktop only: pin last item when section end is on screen.
    // Mobile stack skips this — short items make section bottom enter the
    if (!isMobileStack) {
      const { bottom } = element.getBoundingClientRect();

      if (bottom <= viewportHeight) {
        this.setActiveItem(state, items.length - 1);
        return;
      }
    }

    // Mobile (stack): activate the next item only after the previous item's
    // entire content has scrolled past the sticky media (zoneTop).
    if (isMobileStack) {
      const zoneTop = headerHeight + this.getMobileMediaHeight(state);
      let newActiveIndex = 0;

      contentWrappers.forEach((wrapper, index) => {
        if (!wrapper || index === 0) return;

        const prev = contentWrappers[index - 1];

        if (prev && prev.getBoundingClientRect().bottom <= zoneTop) {
          newActiveIndex = index;
        }
      });

      this.setActiveItem(state, newActiveIndex);
      return;
    }

    let threshold = config.threshold;
    let newActiveIndex = state.currentIndex;

    contentWrappers.forEach((wrapper, index) => {
      if (!wrapper) return;

      const rect = wrapper.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, headerHeight);
      const visibleBottom = Math.min(rect.bottom, viewportHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibility = visibleHeight / rect.height;

      if (visibility >= threshold) {
        newActiveIndex = index;
      }
    });

    this.setActiveItem(state, newActiveIndex);
  },

  updateStickyState(state) {
    if (!this.shouldAnimate(state)) {
      this.removeAllSticky(state);
      return;
    }

    const { element, mediaWrappers, itemsContainer, headerHeight, currentIndex, contentWrappers, items } = state;
    const { top, bottom } = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isMobileStack = state.isMobile && state.animateOnMobile;

    const isBeforeSticky = top > headerHeight;
    const isAfterSticky = bottom <= 0;

    let isInStickyRange;
    let isEnding;

    if (isMobileStack) {
      const isLastItem = currentIndex >= items.length - 1;
      const lastContent = contentWrappers[contentWrappers.length - 1];
      const lastBottom = lastContent ? lastContent.getBoundingClientRect().bottom : bottom;
      // Keep the last image sticky until the last slide's copy has scrolled
      // under it — unpin only when that content's bottom hits the viewport
      // bottom. Short last slides (copy already fully on screen) unpin immediately.
      const lastSlideEnded = lastBottom <= viewportHeight;

      isInStickyRange = top <= headerHeight && !isAfterSticky && !(isLastItem && lastSlideEnded);
      isEnding = top <= headerHeight && isLastItem && lastSlideEnded && !isAfterSticky;
    } else {
      isInStickyRange = top <= headerHeight && bottom > viewportHeight;
      isEnding = bottom <= viewportHeight && top <= headerHeight;
    }

    if (itemsContainer) {
      itemsContainer.classList.toggle('-active', isInStickyRange);
    }

    mediaWrappers.forEach((wrapper, index) => {
      if (!wrapper) return;

      if (isBeforeSticky || isAfterSticky) {
        wrapper.classList.remove('-sticky', '-sticky-end');
        return;
      }

      const isCurrentOrBefore = index <= currentIndex;

      if (isInStickyRange) {
        wrapper.classList.toggle('-sticky', isCurrentOrBefore);
        wrapper.classList.remove('-sticky-end');
      } else if (isEnding) {
        wrapper.classList.remove('-sticky');
        if (isMobileStack) {
          wrapper.classList.toggle('-sticky', isCurrentOrBefore);
          setTimeout(() => {
            wrapper.classList.toggle('-sticky-end', index === currentIndex);
          }, 100);
        } else {
          wrapper.classList.toggle('-sticky-end', isCurrentOrBefore);
        }

      } else {
        wrapper.classList.remove('-sticky', '-sticky-end');
      }
    });
  },

  setActiveItem(state, index) {
    if (state.currentIndex === index) return;
    if (!this.shouldAnimate(state)) return;

    const { items } = state;
    const prevIndex = state.currentIndex;

    items.forEach((item, i) => {
      item.classList.toggle('-active', i === index);
      item.classList.toggle('-passed', i < index);
    });

    state.currentIndex = index;

    state.element.dispatchEvent(new CustomEvent('splitscreen:change', {
      detail: {
        index,
        prevIndex,
        item: items[index]
      }
    }));
  },

  removeAllSticky(state) {
    state.itemsContainer?.classList.remove('-active');
    state.mediaWrappers.forEach(wrapper => {
      wrapper?.classList.remove('-sticky', '-sticky-end');
    });
  },

  removeAllActive(state) {
    state.items.forEach(item => {
      item.classList.remove('-active', '-passed');
    });
    state.currentIndex = 0;
  },

  destroy(element) {
    const state = this.container.get(element.ambid);
    if (!state) return;

    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
    }

    if (state.observers.resize) {
      state.observers.resize.disconnect();
    }

    if (state.scrollHandler) {
      window.removeEventListener('scroll', state.scrollHandler);
    }

    if (state.resizeHandler) {
      window.removeEventListener('resize', state.resizeHandler);
    }

    state.items.forEach(item => item.classList.remove('-active', '-passed'));
    state.itemsContainer?.classList.remove('-active');
    state.mediaWrappers.forEach(wrapper => wrapper?.classList.remove('-sticky', '-sticky-end'));

    this.container.delete(element.ambid);
  }
};
